import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';

type LayoutMode = 'sidebar' | 'top';
export type ThemeMode = 'light' | 'dark' | 'system';
export type TableActionsMode = 'buttons' | 'dropdown';
type Preferences = { mode: LayoutMode; menuOrder: string[]; theme: ThemeMode; tableActionsMode: TableActionsMode; navChromeCollapsed: boolean };
const defaults: Preferences = { mode: 'sidebar', menuOrder: [], theme: 'light', tableActionsMode: 'buttons', navChromeCollapsed: false };
const LayoutPreferencesContext = createContext<{
  preferences: Preferences;
  setMode: (mode: LayoutMode) => void;
  setMenuOrder: (order: string[]) => void;
  setTheme: (theme: ThemeMode) => void;
  setTableActionsMode: (mode: TableActionsMode) => void;
  setNavChromeCollapsed: (collapsed: boolean) => void;
} | null>(null);

function readPreferences(key: string, defaultMode: LayoutMode = 'sidebar'): Preferences {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null');
    return {
      mode: parsed?.mode === 'top'
        ? 'top'
        : parsed?.mode === 'sidebar' ? 'sidebar' : defaultMode,
      theme: parsed?.theme === 'dark' || parsed?.theme === 'system' ? parsed.theme : 'light',
      tableActionsMode: parsed?.tableActionsMode === 'dropdown' ? 'dropdown' : 'buttons',
      navChromeCollapsed: parsed?.navChromeCollapsed === true,
      menuOrder: Array.isArray(parsed?.menuOrder)
        ? [...new Set<string>(parsed.menuOrder.filter((id: unknown) => typeof id === 'string'))]
        : [],
    };
  } catch {
    return { ...defaults, mode: defaultMode };
  }
}

// O layout monta este provider com uma key por usuário para isolar as preferências.
export function LayoutPreferencesProvider({ userId, defaultMode = 'sidebar', children }: {
  userId: string;
  defaultMode?: LayoutMode;
  children: ReactNode;
}) {
  const storageKey = `industrializacao-layout-prefs:${userId}`;
  const [preferences, setPreferences] = useState(() => readPreferences(storageKey, defaultMode));

  useLayoutEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = () => {
      const resolved = preferences.theme === 'system' ? (media.matches ? 'dark' : 'light') : preferences.theme;
      document.documentElement.dataset.theme = resolved;
      document.documentElement.style.colorScheme = resolved;
    };
    applyTheme();
    if (preferences.theme === 'system') media.addEventListener('change', applyTheme);
    return () => {
      media.removeEventListener('change', applyTheme);
      document.documentElement.dataset.theme = 'light';
      document.documentElement.style.colorScheme = 'light';
    };
  }, [preferences.theme]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null) setPreferences(readPreferences(storageKey, defaultMode));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [defaultMode, storageKey]);

  const update = useCallback((patch: Partial<Preferences>) => {
    setPreferences((current) => ({ ...current, ...patch }));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch {
      // Continua funcionando nesta sessão quando o navegador bloqueia o armazenamento.
    }
  }, [preferences, storageKey]);

  return (
    <LayoutPreferencesContext.Provider value={{
      preferences,
      setMode: (mode) => update({ mode }),
      setMenuOrder: (menuOrder) => update({ menuOrder }),
      setTheme: (theme) => update({ theme }),
      setTableActionsMode: (tableActionsMode) => update({ tableActionsMode }),
      setNavChromeCollapsed: (navChromeCollapsed) => update({ navChromeCollapsed }),
    }}>
      {children}
    </LayoutPreferencesContext.Provider>
  );
}

export function useLayoutPreferences() {
  const context = useContext(LayoutPreferencesContext);
  if (!context) throw new Error('LayoutPreferencesProvider ausente');
  return context;
}
